import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  LayoutDashboard, ArrowRight, Play, Sparkles,
} from 'lucide-react';
import { fadeInUp, stagger } from './data';

const ROTATING_WORDS = ['Skill Games', 'Slot Machine', 'Casino Game', 'Amusement', 'Claw Machine', 'Coin Pusher'];

const RotatingWord = () => {
  const [index, setIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const word = ROTATING_WORDS[index];
    const speed = isDeleting ? 40 : 80;

    if (!isDeleting && displayText === word) {
      const pause = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(pause);
    }

    if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
      return;
    }

    const timer = setTimeout(() => {
      setDisplayText(isDeleting ? word.slice(0, displayText.length - 1) : word.slice(0, displayText.length + 1));
    }, speed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, index]);

  return (
    <span className="inline-block min-w-[200px] md:min-w-[340px] text-left">
      {displayText}
      <span className="animate-pulse text-amber-400">|</span>
    </span>
  );
};

export const HeroSection = ({ setDemoModalOpen, heroY }) => (
  <section aria-label="Hero - Skill Games Management Software" className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-amber-500/10 rounded-full blur-[150px] opacity-40" />
      <div className="absolute top-40 right-0 translate-x-1/2 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-yellow-500/10 rounded-full blur-[100px] opacity-30" />
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center max-w-4xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={stagger}
      >
        <motion.div variants={fadeInUp}>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-6 px-4 py-1.5">
            <Sparkles className="w-4 h-4 mr-2" />
            #1 Skill Games Management Software
          </Badge>
        </motion.div>

        <motion.h1
          variants={fadeInUp}
          className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6"
        >
          <RotatingWord />
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500">
            Management Made Easy
          </span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          Complete accounting, invoicing, inventory tracking, machine monitoring,
          and route management — the all-in-one platform for amusement games operators.
        </motion.p>

        <motion.div
          variants={fadeInUp}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/signup">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold px-8 h-14 text-lg shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] transition-all"
              data-testid="hero-cta"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Button
            size="lg"
            variant="outline"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-400 px-8 h-14 text-lg"
            onClick={() => setDemoModalOpen(true)}
            data-testid="watch-demo-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Watch Demo
          </Button>
        </motion.div>
      </motion.div>

      {/* Hero Dashboard Mockup */}
      <motion.div
        className="mt-16 md:mt-24 relative"
        style={{ y: heroY }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="relative max-w-5xl mx-auto">
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 rounded-2xl blur-2xl opacity-50" />

          <div className="relative bg-neutral-950 rounded-xl border border-amber-500/20 overflow-hidden shadow-2xl" data-testid="hero-dashboard-mockup">
            <div className="bg-neutral-900 border-b border-white/5 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <LayoutDashboard className="w-4 h-4 text-amber-400" />
                </div>
                <span className="font-semibold text-white text-sm">SlotFleet Dashboard</span>
                <span className="text-xs text-gray-500 ml-2 hidden sm:inline">Golden State Gaming Co.</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-2 text-xs text-gray-500">
                  <span className="w-2 h-2 rounded-full bg-green-400"></span>
                  All systems online
                </div>
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-amber-400">MJ</span>
                </div>
              </div>
            </div>

            <div className="flex">
              <div className="hidden md:flex flex-col w-44 border-r border-white/5 bg-neutral-900/50 py-4 px-3 gap-1">
                {[
                  { label: 'Dashboard', active: true },
                  { label: 'Locations', active: false },
                  { label: 'Machines', active: false },
                  { label: 'Collections', active: false },
                  { label: 'Reports', active: false },
                  { label: 'Invoices', active: false },
                  { label: 'Routes', active: false },
                ].map((item) => (
                  <div key={item.label} className={`px-3 py-1.5 rounded-md text-xs ${item.active ? 'bg-amber-500/10 text-amber-400 font-semibold' : 'text-gray-500'}`}>
                    {item.label}
                  </div>
                ))}
              </div>

              <div className="flex-1 p-4 md:p-5 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Revenue', value: '$284,720', change: '+18.2%', color: 'text-green-400' },
                    { label: 'Locations', value: '24', change: '+3 new', color: 'text-teal-400' },
                    { label: 'Machines', value: '186', change: '98% active', color: 'text-amber-400' },
                    { label: 'Collections', value: '1,247', change: 'This month', color: 'text-blue-400' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-neutral-900 rounded-lg p-3 border border-white/5">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{stat.label}</div>
                      <div className={`text-base md:text-lg font-bold ${stat.color}`}>{stat.value}</div>
                      <div className="text-[10px] text-gray-500">{stat.change}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-3 bg-neutral-900 rounded-lg border border-white/5 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-white">Revenue Trend</span>
                      <span className="text-[10px] text-gray-500">Last 6 months</span>
                    </div>
                    <div className="flex items-end gap-2 h-24">
                      {[40, 55, 45, 65, 58, 78].map((h, i) => (
                        <div key={`chart-bar-${i}`} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full rounded-t bg-gradient-to-t from-amber-500/60 to-amber-500/20" style={{ height: `${h}%` }} />
                          <span className="text-[8px] text-gray-600">{['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'][i]}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2 bg-neutral-900 rounded-lg border border-white/5 p-4">
                    <span className="text-xs font-semibold text-white">Recent Collections</span>
                    <div className="mt-3 space-y-2">
                      {[
                        { loc: 'Lucky Bar & Grill', amt: '$2,450' },
                        { loc: 'Metro Lanes', amt: '$1,870' },
                        { loc: 'Sunset Tavern', amt: '$3,120' },
                        { loc: 'Downtown Pub', amt: '$1,640' },
                      ].map((c) => (
                        <div key={c.loc} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                          <span className="text-[11px] text-gray-400">{c.loc}</span>
                          <span className="text-[11px] text-green-400 font-medium">{c.amt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            className="absolute -left-4 md:-left-8 top-1/4 hidden lg:block"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Card className="bg-neutral-900/90 backdrop-blur border-amber-500/20 shadow-xl">
              <CardContent className="p-4">
                <div className="text-sm text-gray-400">Total Collections</div>
                <div className="text-2xl font-bold text-green-400">$124,580</div>
                <div className="text-xs text-green-400">+12.5% this month</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            className="absolute -right-4 md:-right-8 bottom-1/4 hidden lg:block"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            <Card className="bg-neutral-900/90 backdrop-blur border-amber-500/20 shadow-xl">
              <CardContent className="p-4">
                <div className="text-sm text-gray-400">Machines Tracked</div>
                <div className="text-2xl font-bold text-amber-400">2,847</div>
                <div className="text-xs text-amber-400">Across 156 locations</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
);
